import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Plus, Minus, Pencil, CheckCircle2, Rocket } from "lucide-react";

const CAP = 6;

function Row({ icon: Icon, color, title, detail, testid }) {
  return (
    <div data-testid={testid} className="flex items-start gap-1.5 text-xs py-0.5">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color}`} />
      <span className="min-w-0 text-[color:var(--ink-2)]">
        <span className="font-medium truncate">{title}</span>
        {detail ? <span className="text-[color:var(--ink-3)]"> — {detail}</span> : null}
      </span>
    </div>
  );
}

// Summary of what changed vs the currently published snapshot — shown before publishing.
export function PublishDiffSummary({ dashboardId, open }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) { setDiff(null); return; }
    setLoading(true);
    api.get(`/dashboards/${dashboardId}/publish-diff`)
      .then(({ data }) => setDiff(data))
      .catch(() => setDiff(null))
      .finally(() => setLoading(false));
  }, [open, dashboardId]);

  if (loading) return <div className="flex items-center gap-1.5 text-xs text-[color:var(--ink-3)] py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Comparing with published version…</div>;
  if (!diff) return null;

  if (diff.first_publish) {
    return (
      <div data-testid="publish-diff-first" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 py-2 text-xs text-[color:var(--ink-2)] flex items-center gap-1.5">
        <Rocket className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />
        First publish — all {diff.widgets_total} widget{diff.widgets_total === 1 ? "" : "s"} go live.
      </div>
    );
  }
  if (diff.clean) {
    return (
      <div data-testid="publish-diff-nochanges" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 py-2 text-xs text-[color:var(--ink-2)] flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />
        No changes since the last publish — republishing keeps the same content.
      </div>
    );
  }
  const extra = [];
  if (diff.name_changed) extra.push("dashboard name");
  if (diff.filters_changed) extra.push("global filters");
  if (diff.canvas_changed) extra.push("canvas settings");
  return (
    <div data-testid="publish-diff-summary" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 py-2 max-h-44 overflow-y-auto">
      <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] mb-1">Changes since last publish</div>
      {(diff.added || []).slice(0, CAP).map((w) => (
        <Row key={w.id} icon={Plus} color="text-[color:var(--brand-mint)]" title={w.title} detail="new widget" testid="publish-diff-added" />
      ))}
      {(diff.removed || []).slice(0, CAP).map((w) => (
        <Row key={w.id} icon={Minus} color="text-[color:var(--danger)]" title={w.title} detail="removed" testid="publish-diff-removed" />
      ))}
      {(diff.changed || []).slice(0, CAP).map((w) => (
        <Row key={w.id} icon={Pencil} color="text-[color:var(--brand-ocean)]" title={w.title} detail={(w.aspects || []).join(", ")} testid="publish-diff-changed" />
      ))}
      {(diff.added.length + diff.removed.length + diff.changed.length) > CAP * 3 && (
        <div className="text-[11px] text-[color:var(--ink-3)] pt-0.5">…and more</div>
      )}
      {extra.length > 0 && (
        <div data-testid="publish-diff-extra" className="text-[11px] text-[color:var(--ink-3)] pt-1 border-t border-[color:var(--glass-border)] mt-1">
          Also changed: {extra.join(" · ")}
        </div>
      )}
    </div>
  );
}
